import { fx_crearBaseLocal, reqToPromise, txDone } from "../../../../utils/idbUtils";

const DB_NAME = "grpc_monitoreo_relacion_finca_plaga";
const DB_VERSION = 4;
const STORE_RELACIONES = "relaciones";
const STORE_SYNC = "sync";

/** Item mínimo que necesita el merge de delta (mismo contrato que
 *  `GrpcLoteItem`/`GrpcAplicacionItem`: watermark por finca, no uno global). */
export type GrpcMonitoreoRelacionItem = {
  id: string;
  eliminado: boolean;
  ultimaActualizacion: bigint;
};

/** IndexedDB **nueva y separada** para las relaciones finca-cultivo-plaga que
 *  trae `MonitoreoRelacionFincaCultivoPlagaGrpcService` (ver CLAUDE.md del
 *  Server, sección migración a gRPC-Web) — mismo patrón que
 *  `GrpcLoteDatabase`/`GrpcAplicacionDatabase`: delta real por `fincaId`. */
class GrpcMonitoreoRelacionFincaCultivoPlagaDatabase {
  /** Conexión a la base local con el ayudante común, que al cambiar de
   *  versión conserva los datos (ver `fx_crearBaseLocal`). La versión 3
   *  (2026-09-30) se subió a pedido del usuario para una limpieza única al
   *  estrenar el ayudante: un navegador con una versión anterior vacía la
   *  base una sola vez y la vuelve a llenar desde el servidor.
   *
   *  **Versión 4 (2026-10-23): cambió la forma del item.** La relación ahora
   *  trae los cuatro valores de referencia del cliente, su versión y quién los
   *  cambió. Las relaciones ya cacheadas no los tienen, el delta no las vuelve a
   *  pedir —no cambiaron en el servidor— y leer un valor de un item que no lo
   *  trae deja el panel de configuración en blanco sin decir por qué. */
  private readonly _base = fx_crearBaseLocal(
    DB_NAME,
    DB_VERSION,
    [
      { nombre: STORE_RELACIONES, clave: "id", indices: ["fincaId"] },
      { nombre: STORE_SYNC, clave: "fincaId" },
    ],
    { vaciarAntesDe: 4 },
  );

  /** Conexión a la base local, abierta la primera vez y reusada después. */
  private open(): Promise<IDBDatabase> {
    return this._base.abrir();
  }

  async getUltimaSincronizacionAsync(fincaId: string): Promise<bigint> {
    const db = await this.open();
    const store = db.transaction(STORE_SYNC, "readonly").objectStore(STORE_SYNC);
    const row = await reqToPromise<{ fincaId: string; ultimaSincronizacion: string } | undefined>(
      store.get(fincaId),
    );
    return row ? BigInt(row.ultimaSincronizacion) : 0n;
  }

  async putDeltaAsync<T extends GrpcMonitoreoRelacionItem>(
    fincaId: string,
    items: readonly T[],
    nuevoWatermark: bigint,
  ): Promise<void> {
    const db = await this.open();
    const tx = db.transaction([STORE_RELACIONES, STORE_SYNC], "readwrite");
    const store = tx.objectStore(STORE_RELACIONES);
    const syncStore = tx.objectStore(STORE_SYNC);

    for (const item of items) {
      if (item.eliminado) {
        store.delete(item.id);
      } else {
        store.put(item);
      }
    }
    syncStore.put({ fincaId, ultimaSincronizacion: nuevoWatermark.toString() });

    await txDone(tx);
  }

  async getListByFincaIdAsync<T>(fincaId: string): Promise<T[]> {
    const db = await this.open();
    const index = db
      .transaction(STORE_RELACIONES, "readonly")
      .objectStore(STORE_RELACIONES)
      .index("fincaId");
    return reqToPromise<T[]>(index.getAll(fincaId));
  }

  /** Actualiza (o inserta) una sola relación sin tocar el resto (alta de una
   *  plaga: `CultivoPlagasRender` la pinta al toque sin esperar el próximo
   *  delta). */
  async putOneAsync<T extends GrpcMonitoreoRelacionItem>(item: T): Promise<void> {
    const db = await this.open();
    const tx = db.transaction(STORE_RELACIONES, "readwrite");
    tx.objectStore(STORE_RELACIONES).put(item);
    await txDone(tx);
  }

  /** Borra una relación puntual (baja confirmada por el server: apagar una
   *  plaga es un `DELETE`, no hay estado intermedio). */
  async deleteOneAsync(id: string): Promise<void> {
    const db = await this.open();
    const tx = db.transaction(STORE_RELACIONES, "readwrite");
    tx.objectStore(STORE_RELACIONES).delete(id);
    await txDone(tx);
  }
}

/** Singleton de la base de relaciones finca-cultivo-plaga por gRPC-Web (con
 *  delta por finca). */
export const grpcMonitoreoRelacionFincaCultivoPlagaDatabase =
  new GrpcMonitoreoRelacionFincaCultivoPlagaDatabase();
